import { Button, Col, Row, Form, FormGroup, FormLabel, Modal } from "react-bootstrap";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, UseFormReturn, useWatch } from "react-hook-form";
import { ButtonContainer, ModalHeader, StyledFormControl, StyledModal } from "../styles";
import { FormattedMessage } from "react-intl";
import { messages } from "../messages";
import { StyledSpinner } from "../../reservationDetail/styles";
import { InputMultiCheckbox } from "../../bundle/components/InputMultiCheckbox";
import { OccupationEntity } from "../../occupation/domain/occupation.entity";
import {
  GenerateCombinationsProps,
  hasValidOccupancyLimits,
  OccupancyGeneratorProps,
} from "./OccupancyGenerator";

type CombinationModalProps = {
  open: boolean;
  handleHide: () => void;
  handleSave: () => Promise<boolean>;
  methods: UseFormReturn<OccupancyGeneratorProps>;
  locale: string;
  onGenerateCombinations: (props: GenerateCombinationsProps) => Promise<OccupationEntity[]>;
  onPruneSelectedCombinations: (combinations: OccupationEntity[]) => string[];
};

export default function CombinationModal({
  open,
  handleHide,
  handleSave,
  locale,
  methods,
  onGenerateCombinations,
  onPruneSelectedCombinations,
}: CombinationModalProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [combinationsGenerated, setCombinationsGenerated] = useState<OccupationEntity[]>([]);
  const [error, setError] = useState<"noValidSelection" | "loadFailed" | null>(null);

  const occupancyCombinationsList = useWatch({
    control: methods.control,
    name: "occupancyCombinationsList",
  });
  const maxOccupancy = useWatch({ control: methods.control, name: "maxOccupancy" }) || 1;
  const maxOccupancyByType = useWatch({ control: methods.control, name: "maxOccupancyByType" });

  const getDisplayMultiCheckbox = (shortLocale: string) => ({
    translationsList,
  }: OccupationEntity) => {
    return (
      translationsList.find(({ locale }) => locale === shortLocale)?.name ||
      translationsList[0].name
    );
  };

  const generateCombinations = useCallback(async () => {
    const combinations = await onGenerateCombinations({ maxOccupancy, maxOccupancyByType });
    setCombinationsGenerated(combinations);
    return combinations;
  }, [maxOccupancy, maxOccupancyByType, onGenerateCombinations]);

  const handleOnClickListCombinations = useCallback(async () => {
    setIsProcessing(true);
    setError(null);
    try {
      const combinations = await generateCombinations();
      onPruneSelectedCombinations(combinations);
    } catch {
      setError("loadFailed");
    } finally {
      setIsProcessing(false);
    }
  }, [generateCombinations, onPruneSelectedCombinations]);

  const handleOnClick = useCallback(async () => {
    setIsProcessing(true);
    setError(null);
    try {
      const saved = await handleSave();
      if (!saved) {
        // The selection was emptied, so refresh the options the user can pick from.
        setError("noValidSelection");
        await generateCombinations();
      }
    } catch {
      setError("loadFailed");
    } finally {
      setIsProcessing(false);
    }
  }, [generateCombinations, handleSave]);

  const isDisabled = useMemo(() => {
    return (occupancyCombinationsList || []).length === 0;
  }, [occupancyCombinationsList]);

  const isAvailableToGenerate = useMemo(
    () => hasValidOccupancyLimits({ maxOccupancy, maxOccupancyByType }),
    [maxOccupancy, maxOccupancyByType]
  );

  useEffect(() => {
    if (!open) setError(null);
  }, [open]);

  useEffect(() => {
    if (open && occupancyCombinationsList && occupancyCombinationsList.length > 0) {
      generateCombinations()
        .then(() => console.debug("combinations generated"))
        .catch(() => setError("loadFailed"));
    }
  }, [generateCombinations, occupancyCombinationsList, open]);

  return (
    <Form noValidate>
      <StyledModal show={open} onHide={handleHide} centered={true}>
        <ModalHeader closeButton>
          <FormattedMessage {...messages.accommodationOccupancySelection} />
        </ModalHeader>
        <Modal.Body style={{ fontSize: 13 }} className="ml-3 mr-3">
          <Row className="flex flex-col center-items">
            <FormGroup>
              <FormLabel>
                <FormattedMessage {...messages.accommodationMaxOccupationTitle} />*
              </FormLabel>
              <Controller
                name="maxOccupancy"
                control={methods.control}
                render={({ field }) => (
                  <>
                    <StyledFormControl
                      type="number"
                      className="rounded-right w-full"
                      defaultValue={1}
                      min={1}
                      {...field}
                      onChange={event => field.onChange(Number(event.target.value))}
                    />
                    <Form.Control.Feedback type="invalid">
                      <FormattedMessage
                        {...messages.maxCombinationParamsValidationFailed}
                        values={{ max: maxOccupancy }}
                      />
                    </Form.Control.Feedback>
                  </>
                )}
              />
            </FormGroup>
            <Row className="flex flex-row">
              <FormGroup as={Col}>
                <FormLabel>
                  <FormattedMessage {...messages.accommodationMaxOccupationAdults} />*
                </FormLabel>
                <Controller
                  name="maxOccupancyByType.adults"
                  control={methods.control}
                  render={({ field }) => (
                    <>
                      <StyledFormControl
                        type="number"
                        className="rounded-right w-full"
                        defaultValue={1}
                        min={0}
                        max={maxOccupancy}
                        isInvalid={(maxOccupancyByType?.adults || 0) > maxOccupancy}
                        {...field}
                        onChange={event => field.onChange(Number(event.target.value))}
                      />
                      <Form.Control.Feedback type="invalid">
                        <FormattedMessage
                          {...messages.maxCombinationParamsValidationFailed}
                          values={{ max: maxOccupancy }}
                        />
                      </Form.Control.Feedback>
                    </>
                  )}
                />
              </FormGroup>

              <FormGroup as={Col}>
                <FormLabel>
                  <FormattedMessage {...messages.accommodationMaxOccupationJuniors} />*
                </FormLabel>
                <Controller
                  name="maxOccupancyByType.juniors"
                  control={methods.control}
                  render={({ field }) => (
                    <>
                      <StyledFormControl
                        type="number"
                        className="rounded-right w-full"
                        defaultValue={0}
                        min={0}
                        max={maxOccupancy}
                        isInvalid={(maxOccupancyByType?.juniors || 0) > maxOccupancy}
                        {...field}
                        onChange={event => field.onChange(Number(event.target.value))}
                      />
                      <Form.Control.Feedback type="invalid">
                        <FormattedMessage
                          {...messages.maxCombinationParamsValidationFailed}
                          values={{ max: maxOccupancy }}
                        />
                      </Form.Control.Feedback>
                    </>
                  )}
                />
              </FormGroup>

              <FormGroup as={Col}>
                <FormLabel>
                  <FormattedMessage {...messages.accommodationMaxOccupationChildren} />*
                </FormLabel>
                <Controller
                  name="maxOccupancyByType.children"
                  control={methods.control}
                  render={({ field }) => (
                    <>
                      <StyledFormControl
                        type="number"
                        className="rounded-right w-full"
                        defaultValue={0}
                        min={0}
                        max={maxOccupancy}
                        isInvalid={(maxOccupancyByType?.children || 0) > maxOccupancy}
                        {...field}
                        onChange={event => field.onChange(Number(event.target.value))}
                      />
                      <Form.Control.Feedback type="invalid">
                        <FormattedMessage
                          {...messages.maxCombinationParamsValidationFailed}
                          values={{ max: maxOccupancy }}
                        />
                      </Form.Control.Feedback>
                    </>
                  )}
                />
              </FormGroup>

              <FormGroup as={Col}>
                <FormLabel>
                  <FormattedMessage {...messages.accommodationMaxOccupationBabies} />*
                </FormLabel>
                <Controller
                  name="maxOccupancyByType.babies"
                  control={methods.control}
                  render={({ field }) => (
                    <>
                      <StyledFormControl
                        type="number"
                        className="rounded-right w-full"
                        defaultValue={0}
                        min={0}
                        max={maxOccupancy}
                        isInvalid={(maxOccupancyByType?.babies || 0) > maxOccupancy}
                        {...field}
                        onChange={event => field.onChange(Number(event.target.value))}
                      />
                      <Form.Control.Feedback type="invalid">
                        <FormattedMessage
                          {...messages.maxCombinationParamsValidationFailed}
                          values={{ max: maxOccupancy }}
                        />
                      </Form.Control.Feedback>
                    </>
                  )}
                />
              </FormGroup>
            </Row>
            <ButtonContainer>
              <Button
                variant="primary"
                type="button"
                onClick={handleOnClickListCombinations}
                disabled={isProcessing || !isAvailableToGenerate}>
                {isProcessing ? (
                  <StyledSpinner className="fa fa-spinner fa-spin" />
                ) : (
                  <FormattedMessage {...messages.accommodationOccupancyList} />
                )}
              </Button>
            </ButtonContainer>
            {combinationsGenerated.length > 0 && (
              <>
                <hr />
                <Controller
                  name="occupancyCombinationsList"
                  control={methods.control}
                  render={({ field }) => (
                    <InputMultiCheckbox
                      getDisplay={getDisplayMultiCheckbox(locale)}
                      className="witadmin-bs3"
                      locale={locale}
                      field={{
                        fieldName: `inventorySelectedMultiCheckbox`,
                        options: combinationsGenerated || [],
                        selectables: true,
                      }}
                      {...field}
                      onChange={(value: string[]) => {
                        setError(null);
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              </>
            )}
            {error && (
              <Form.Text className="text-danger d-block">
                <FormattedMessage
                  {...(error === "loadFailed"
                    ? messages.accommodationOccupancyLoadFailed
                    : messages.accommodationNoValidOccupancySelected)}
                />
              </Form.Text>
            )}
            <ButtonContainer alignRight={true}>
              <Button
                variant="primary"
                type="button"
                onClick={handleOnClick}
                disabled={isDisabled || isProcessing}>
                <FormattedMessage {...messages.save} />
              </Button>
            </ButtonContainer>
          </Row>
        </Modal.Body>
      </StyledModal>
    </Form>
  );
}
